import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import type { CategoryValidityMode } from '@/types';
import { VALIDITY_MODE_OPTIONS } from '@/constants/validityMode';

/** The empty option's value — MUI Select cannot carry `null` directly. */
const NONE_SELECTED = '';

interface ValidityModeSelectProps {
  /** The pre-selection, or null for "no pre-selection". */
  value: CategoryValidityMode | null;
  onChange: (mode: CategoryValidityMode | null) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

const isMode = (value: string): value is CategoryValidityMode =>
  VALIDITY_MODE_OPTIONS.some((option) => option.value === value);

/**
 * Admin picker for the validity mode a requirement or upload field pre-selects
 * (AG-249). Without one, an annual journal requirement never shows year chips
 * and every upload opens on "Tills vidare" — which is why it is the same
 * control in both editors rather than a per-editor Select.
 */
export default function ValidityModeSelect({
  value,
  onChange,
  size = 'medium',
  disabled = false,
}: ValidityModeSelectProps) {
  const selected = value ?? NONE_SELECTED;
  const hint = VALIDITY_MODE_OPTIONS.find((option) => option.value === value)?.hint;

  return (
    <FormControl fullWidth size={size} disabled={disabled}>
      <InputLabel id="validity-mode-label">Giltighet vid uppladdning</InputLabel>
      <Select
        labelId="validity-mode-label"
        label="Giltighet vid uppladdning"
        value={selected}
        onChange={(event) => {
          const next = event.target.value;
          onChange(isMode(next) ? next : null);
        }}
      >
        <MenuItem value={NONE_SELECTED}>
          <em>Inget förval</em>
        </MenuItem>
        {VALIDITY_MODE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {hint ?? 'Förvalt läge när lantbrukaren laddar upp mot detta. Lantbrukaren kan alltid byta.'}
      </FormHelperText>
    </FormControl>
  );
}
